// "Raise an issue" — one entry point, three kinds, from every screen.
//
// The old Report sheet was two buttons and a block control, reached only from
// the home screen's footer. This is one sheet reached from three places — the
// foot of the tablet rail, the top of the More tab, and the pod in hand — and
// it is the same three kinds in each, so "something is wrong" always leads to
// the same picker. On a phone the centre of the tab bar went to Scan, which is
// used a hundred times a day to Raise's once; the More tab draws these kinds as
// cards at its top so that once is still two taps.
//
// Adding a fourth kind is one more row, which is the point: the andon app and
// this one merge eventually, and the type picker is the seam they meet at.

import type { ReactNode } from 'react';
import { Sheet } from '@/components/ui/Sheet';
import { Icon, type IconName } from '@/components/ui/Icon';
import { StatChip } from '@/components/home/Score';
import { useT } from '@/i18n/useT';
import type { PodVM } from '@/api/types';
import { useStore } from '@/store';

export type RaiseKind = 'andon' | 'quality' | 'safety';

export function RaiseSheet({
  open,
  pod,
  onClose,
  onChoose,
}: {
  open: boolean;
  /** The pod in front of the operator, pre-filled into whatever they raise. */
  pod: PodVM | undefined;
  onClose: () => void;
  onChoose: (kind: RaiseKind) => void;
}) {
  const t = useT();
  const wcName = useStore((s) => s.workcentreName);
  const flName = useStore((s) => s.flowlineName);

  const context = [wcName, flName, pod?.mo].filter(Boolean).join(' · ');

  return (
    <Sheet open={open} onClose={onClose} title={t.raiseAnIssueT} hideTitle>
      <div style={{ marginBottom: 12 }}>
        <div style={{ font: "700 18px 'Space Grotesk',sans-serif" }}>{t.raiseAnIssueT}</div>
        <div style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', marginTop: 3 }}>
          {context ? `${t.prefilledWithT} ${context}` : t.prefilledDefaultT}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 12 }}>
        <TypeRow
          icon="hazard"
          title={t.andonRowTitleT}
          sub={t.andonRowSubT}
          tone="danger"
          onClick={() => onChoose('andon')}
        />
        <TypeRow
          icon="checkCircle"
          title={t.qualityRowTitleT}
          sub={t.qualityRowSubT}
          tone="warn"
          onClick={() => onChoose('quality')}
        />
        <TypeRow
          icon="shield"
          title={t.safetyRowTitleT}
          sub={t.safetyRowSubT}
          tone="neutral"
          onClick={() => onChoose('safety')}
          trailing={
            <StatChip tone="neutral" size="sm">
              {t.newTagT}
            </StatChip>
          }
        />
      </div>

      <button
        type="button"
        className="press"
        onClick={onClose}
        style={{
          width: '100%',
          minHeight: 44,
          borderRadius: 14,
          border: '1px solid var(--line)',
          display: 'grid',
          placeItems: 'center',
          font: '700 13px Manrope,sans-serif',
          color: 'var(--sub)',
        }}
      >
        {t.cancelT}
      </button>
    </Sheet>
  );
}

function TypeRow({
  icon,
  title,
  sub,
  tone,
  onClick,
  trailing,
}: {
  icon: IconName;
  title: string;
  sub: string;
  tone: 'danger' | 'warn' | 'neutral';
  onClick: () => void;
  trailing?: ReactNode;
}) {
  const lead =
    tone === 'danger'
      ? { background: 'var(--danger)', color: '#fff' }
      : tone === 'warn'
        ? { background: 'var(--warnSoft)', color: 'var(--warn)' }
        : { background: 'var(--surface2)', color: 'var(--ink)' };

  return (
    <button
      type="button"
      className="press-row"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        minHeight: 64,
        padding: '14px 15px',
        borderRadius: 16,
        border: tone === 'danger' ? '1px solid var(--danger)' : '1px solid var(--line)',
        background: tone === 'danger' ? 'var(--dangerSoft)' : 'var(--surface)',
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
          ...lead,
        }}
      >
        <Icon name={icon} size={20} strokeWidth={2} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            font: '700 14px Manrope,sans-serif',
            color: tone === 'danger' ? 'var(--danger)' : 'var(--ink)',
          }}
        >
          {title}
        </span>
        <span style={{ display: 'block', font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)' }}>{sub}</span>
      </span>
      {trailing ?? (
        <span aria-hidden="true" style={{ color: tone === 'danger' ? 'var(--danger)' : 'var(--sub)', display: 'flex' }}>
          <Icon name="chevron" size={15} />
        </span>
      )}
    </button>
  );
}

/**
 * What happens behind the Andon row.
 *
 * Raising an andon starts a clock, a notification ladder and an SLA that the
 * Andon app owns end to end. Creating the record from here would start the
 * record without the machinery, so the row hands over instead — carrying the
 * station, the flowline and the pod so nothing has to be typed twice.
 */
export function AndonHandoffSheet({
  open,
  moId,
  onClose,
}: {
  open: boolean;
  /** The pod the andon is about — the one in hand, or a quality alert's. */
  moId: number | null;
  onClose: () => void;
}) {
  const t = useT();
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const workcentreId = useStore((s) => s.workcentreId);
  const wcName = useStore((s) => s.workcentreName);
  const flowlineId = useStore((s) => s.flowlineId);

  const href = andonAppUrl
    ? `${andonAppUrl}/raise?${new URLSearchParams({
        ...(workcentreId ? { workcenter_id: String(workcentreId) } : {}),
        ...(flowlineId ? { flowline_id: String(flowlineId) } : {}),
        ...(moId ? { production_id: String(moId) } : {}),
      }).toString()}`
    : null;

  return (
    <Sheet open={open} onClose={onClose} title={t.andonRowTitleT}>
      {href ? (
        <>
          <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 0 }}>
            {t.andonHandoffT}
          </p>
          <a
            href={href}
            className="press-soft"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              minHeight: 52,
              borderRadius: 15,
              background: 'var(--danger)',
              color: '#fff',
              font: "700 15px 'Space Grotesk',sans-serif",
              textDecoration: 'none',
            }}
          >
            <Icon name="hazard" size={18} strokeWidth={2} />
            {t.raiseAndonAtT} {wcName ?? ''}
            <Icon name="external" size={15} />
          </a>
        </>
      ) : (
        <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 0 }}>
          {t.andonNotConfiguredT}
        </p>
      )}
    </Sheet>
  );
}

/**
 * The WHS incident report.
 *
 * There is no `safety.alert` model in Odoo, and the report that WHS actually
 * works from lives outside it, so the picker hands straight over to that form
 * in a new tab: the operator keeps their place in the app, and the report
 * lands where someone reads it.
 */
export const SAFETY_INCIDENT_REPORT_URL = 'https://interpod.link/IncidentReport';

export function openSafetyIncidentReport() {
  // An anchor click rather than window.open: it inherits the tap that got us
  // here, so the popup blocker leaves it alone, and `noopener` keeps the new
  // tab from reaching back into this one.
  const a = document.createElement('a');
  a.href = SAFETY_INCIDENT_REPORT_URL;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.click();
}
